'use no memo';

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import type { DragEndEvent, Modifier } from '@dnd-kit/core';
import type { ReactNode } from 'react';

const snapToRowHeight: Modifier = ({ transform, draggingNodeRect }) => {
  const rowHeight = draggingNodeRect?.height;
  if (!rowHeight) {
    return transform;
  }
  return {
    ...transform,
    y: Math.round(transform.y / rowHeight) * rowHeight,
  };
};

type TableDndContextProps = {
  rowIds: string[];
  onReorder: (orderedIds: string[]) => void;
  children: ReactNode;
};

export const TableDndContext = (props: TableDndContextProps) => {
  const { rowIds, onReorder, children } = props;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = rowIds.indexOf(String(active.id));
    const newIndex = rowIds.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    onReorder(arrayMove(rowIds, oldIndex, newIndex));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      autoScroll={false}
      modifiers={[
        restrictToVerticalAxis,
        snapToRowHeight,
        restrictToParentElement,
      ]}
    >
      <SortableContext
        items={rowIds}
        strategy={verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
};
